import Papa from "papaparse"

import type {
  AlfredBulkImportIssue,
  AlfredBulkImportRow,
  AlfredBulkImportValidationResult,
} from "@/types/alfred-bulk-import"
import {
  getAlfredMaxLengthError,
  MAX_ALFRED_INDEXED_STRING_LENGTH,
} from "@/lib/alfred-field-validation"

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024
const MAX_DATA_ROWS = 10_000
const REQUIRED_HEADERS = ["clientId", "deviceId", "sotiDeviceId"] as const
const SUPPORTED_HEADERS = new Set([
  "clientId",
  "deviceId",
  "sotiDeviceId",
  "league",
  "areaCode",
  "facilityCode",
  "deviceCode",
  "validationVendor",
  "proVenueUser",
  "ticketmasterVenueId",
  "ticketmasterConsumerKey",
  "mainBackgroundColor",
  "secondaryBackgroundColor",
  "idleStateColor",
  "operatingMode",
  "turnstileMode",
  "offlineValidationMode",
  "exitScanningAllowed",
  "isLockFailSafe",
  "useSimpleResponse",
  "nfcEnabled",
  "enableFan",
  "enableHotspot",
  "wakeLockEnabled",
  "enableScanReset",
  "enableManualValidationMask",
  "offlineBatchUploadLimit",
  "soundValidNumber",
  "soundInvalidNumber",
  "soundWarningNumber",
  "maxCpuTemperature",
  "minCpuTemperature",
  "adminModeIdleWindow",
  "validDisplayTime",
  "invalidDisplayTime",
  "unlockedAccessTime",
])

interface AlfredBulkImportValidationOptions {
  requiredClientId?: string
  validClientIds?: ReadonlySet<string>
}

export function validateAlfredBulkImportFile(
  file: File,
  options: AlfredBulkImportValidationOptions = {},
): Promise<AlfredBulkImportValidationResult> {
  if (!file.name.toLowerCase().endsWith(".csv")) {
    return Promise.resolve({
      headers: [],
      rows: [],
      issues: [{ message: "Choose a file with a .csv extension." }],
      isValid: false,
    })
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    return Promise.resolve({
      headers: [],
      rows: [],
      issues: [{ message: "CSV files must be 10 MB or smaller." }],
      isValid: false,
    })
  }

  if (file.size === 0) {
    return Promise.resolve({
      headers: [],
      rows: [],
      issues: [{ message: "CSV file is empty." }],
      isValid: false,
    })
  }

  return new Promise((resolve) => {
    Papa.parse<string[]>(file, {
      skipEmptyLines: "greedy",
      complete: ({ data, errors }) => {
        const [rawHeaders = [], ...rawRows] = data
        const headers = rawHeaders.map((header) => header.trim())
        const rows: AlfredBulkImportRow[] = rawRows.map((values) =>
          Object.fromEntries(
            headers.map((header, index) => [header, values[index]?.trim() ?? ""]),
          ),
        )
        const issues: AlfredBulkImportIssue[] = errors.map((error) => {
          const rowNumber = error.row === undefined ? undefined : error.row + 1
          return {
            ...(rowNumber === undefined ? {} : { rowNumber }),
            message: rowNumber
              ? `Row ${rowNumber}: ${error.message}`
              : `CSV parse error: ${error.message}`,
          }
        })
        const headerSet = new Set(headers)
        const seenHeaders = new Set<string>()
        const reportedDuplicateHeaders = new Set<string>()
        const firstRowByDevice = new Map<string, number>()
        const firstRowBySotiDeviceId = new Map<string, number>()

        rawRows.forEach((values, rowIndex) => {
          if (values.length !== headers.length) {
            const rowNumber = rowIndex + 2
            issues.push({
              rowNumber,
              message: `Row ${rowNumber}: expected ${headers.length} values but found ${values.length}.`,
            })
          }
        })

        if (headers.includes("")) {
          issues.push({ message: "CSV contains an empty column header." })
        }

        for (const requiredHeader of REQUIRED_HEADERS) {
          if (!headerSet.has(requiredHeader)) {
            issues.push({
              field: requiredHeader,
              message: `Missing required column "${requiredHeader}".`,
            })
          }
        }

        for (const header of headers) {
          if (seenHeaders.has(header) && !reportedDuplicateHeaders.has(header)) {
            issues.push({
              field: header,
              message: `Duplicate column "${header}".`,
            })
            reportedDuplicateHeaders.add(header)
          }
          seenHeaders.add(header)

          if (header && !SUPPORTED_HEADERS.has(header)) {
            issues.push({
              field: header,
              message: `Unsupported column "${header}".`,
            })
          }
        }

        if (rows.length > MAX_DATA_ROWS) {
          issues.push({
            message: `CSV files may contain at most 10,000 data rows; found ${rows.length.toLocaleString("en-US")}.`,
          })
        }

        rows.forEach((row, rowIndex) => {
          const rowNumber = rowIndex + 2
          for (const requiredHeader of REQUIRED_HEADERS) {
            if (headerSet.has(requiredHeader) && !row[requiredHeader]) {
              issues.push({
                rowNumber,
                field: requiredHeader,
                message: `Row ${rowNumber}: ${requiredHeader} is required.`,
              })
            }
          }

          const clientId = row.clientId
          const deviceId = row.deviceId
          const sotiDeviceId = row.sotiDeviceId
          if (deviceId && getAlfredMaxLengthError("deviceId", deviceId)) {
            issues.push({
              rowNumber,
              field: "deviceId",
              message: `Row ${rowNumber}: deviceId cannot be longer than ${MAX_ALFRED_INDEXED_STRING_LENGTH} characters.`,
            })
          }
          if (clientId && options.validClientIds && !options.validClientIds.has(clientId)) {
            issues.push({
              rowNumber,
              field: "clientId",
              message: `Row ${rowNumber}: unknown clientId "${clientId}".`,
            })
          } else if (
            clientId &&
            options.requiredClientId &&
            clientId !== options.requiredClientId
          ) {
            issues.push({
              rowNumber,
              field: "clientId",
              message: `Row ${rowNumber}: clientId must be "${options.requiredClientId}" for this import.`,
            })
          }

          if (clientId && deviceId) {
            const deviceKey = `${clientId}\u0000${deviceId}`
            const firstRowNumber = firstRowByDevice.get(deviceKey)
            if (firstRowNumber) {
              issues.push({
                rowNumber,
                field: "deviceId",
                message: `Row ${rowNumber}: ${clientId} / ${deviceId} duplicates row ${firstRowNumber}.`,
              })
            } else {
              firstRowByDevice.set(deviceKey, rowNumber)
            }
          }

          if (sotiDeviceId) {
            const firstRowNumber = firstRowBySotiDeviceId.get(sotiDeviceId)
            if (firstRowNumber) {
              issues.push({
                rowNumber,
                field: "sotiDeviceId",
                message: `Row ${rowNumber}: sotiDeviceId "${sotiDeviceId}" duplicates row ${firstRowNumber}.`,
              })
            } else {
              firstRowBySotiDeviceId.set(sotiDeviceId, rowNumber)
            }
          }
        })

        if (rows.length === 0) issues.push({ message: "CSV file contains no data rows." })

        resolve({
          headers,
          rows,
          issues,
          isValid: issues.length === 0,
        })
      },
      error: (error) => {
        resolve({
          headers: [],
          rows: [],
          issues: [{ message: `Unable to read CSV: ${error.message}` }],
          isValid: false,
        })
      },
    })
  })
}
